/**
 * FilePanel - Main file manager container
 *
 * Provides:
 * - Toolbar with path breadcrumb, view mode toggle, actions
 * - FileList for browsing
 * - Drag-drop upload zone
 * - Context menu for file operations
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import FileList from './FileList';
import FilePreview from './FilePreview';
import * as commanderApi from '../../services/commanderApi';
import type { FileInfo, ViewMode, FileUploadProgress } from '../../types/commander';

interface FilePanelProps {
  disk: string | null;
  cwd: string;
  onClose: () => void;
}

export default function FilePanel({ disk, cwd, onClose }: FilePanelProps) {
  const [currentPath, setCurrentPath] = useState(cwd || '/');
  const [entries, setEntries] = useState<FileInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
  const [uploadProgress, setUploadProgress] = useState<FileUploadProgress[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; entry: FileInfo } | null>(null);
  const [previewFile, setPreviewFile] = useState<FileInfo | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load directory when disk or path changes
  useEffect(() => {
    if (disk) {
      loadDirectory(disk, currentPath);
    } else {
      setEntries([]);
    }
  }, [disk, currentPath]);

  // React to cwd changes from terminal
  useEffect(() => {
    if (disk && cwd && cwd !== currentPath) {
      setCurrentPath(cwd);
    }
  }, [cwd]);

  // Reset to root when disk changes
  useEffect(() => {
    if (disk) {
      setCurrentPath('/');
      setSelectedPaths(new Set());
    }
  }, [disk]);

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  const loadDirectory = async (diskName: string, path: string) => {
    setLoading(true);
    setError(null);

    try {
      const result = await commanderApi.listDirectory(diskName, path);
      setEntries(result.entries);
    } catch (err: any) {
      setError(err.message || 'Failed to load directory');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => {
    if (disk) {
      loadDirectory(disk, currentPath);
    }
  };

  const navigateTo = (path: string) => {
    setCurrentPath(path);
    setSelectedPaths(new Set());
  };

  const navigateUp = () => {
    if (currentPath === '/') return;
    const parentPath = currentPath.substring(0, currentPath.lastIndexOf('/')) || '/';
    navigateTo(parentPath);
  };

  const handleSelect = (path: string, multi: boolean) => {
    setSelectedPaths(prev => {
      const next = new Set(multi ? prev : []);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const handleOpen = (entry: FileInfo) => {
    if (entry.is_dir) {
      navigateTo(entry.path);
    } else {
      // Open file preview
      setPreviewFile(entry);
    }
  };

  const handleContextMenu = (e: React.MouseEvent, entry: FileInfo) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, entry });
  };

  // Breadcrumb parts
  const pathParts = currentPath.split('/').filter(Boolean);

  // Drag-drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set false if leaving the container
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!disk) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    uploadFiles(files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !disk) return;
    uploadFiles(Array.from(files));
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFiles = async (files: File[]) => {
    if (!disk) return;

    // Initialize progress for all files
    const progress: FileUploadProgress[] = files.map(file => ({
      file,
      filename: file.name,
      progress: 0,
      status: 'pending'
    }));
    setUploadProgress(progress);

    // Upload files sequentially
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      setUploadProgress(prev =>
        prev.map((p, idx) =>
          idx === i ? { ...p, status: 'uploading' } : p
        )
      );

      try {
        await commanderApi.uploadFile(
          disk,
          currentPath,
          file,
          (percent) => {
            setUploadProgress(prev =>
              prev.map((p, idx) =>
                idx === i ? { ...p, progress: percent } : p
              )
            );
          }
        );

        setUploadProgress(prev =>
          prev.map((p, idx) =>
            idx === i ? { ...p, status: 'completed', progress: 100 } : p
          )
        );
      } catch (err: any) {
        setUploadProgress(prev =>
          prev.map((p, idx) =>
            idx === i ? { ...p, status: 'error', error: err.message } : p
          )
        );
      }
    }

    // Refresh directory after all uploads
    refresh();

    // Clear progress after a delay
    setTimeout(() => {
      setUploadProgress([]);
    }, 3000);
  };

  // Context menu actions
  const handleDownload = async () => {
    if (!disk || !contextMenu) return;
    await commanderApi.downloadFile(disk, contextMenu.entry.path, contextMenu.entry.name);
    setContextMenu(null);
  };

  const handleDelete = async () => {
    if (!disk || !contextMenu) return;
    const entry = contextMenu.entry;

    if (!confirm(`Delete ${entry.is_dir ? 'folder' : 'file'} "${entry.name}"?`)) {
      setContextMenu(null);
      return;
    }

    try {
      if (entry.is_dir) {
        await commanderApi.deleteDirectory(disk, entry.path, true);
      } else {
        await commanderApi.deleteFile(disk, entry.path);
      }
      refresh();
    } catch (err: any) {
      setError(err.message);
    }
    setContextMenu(null);
  };

  const handleRename = async () => {
    if (!disk || !contextMenu) return;
    const entry = contextMenu.entry;
    const newName = prompt('Enter new name:', entry.name);

    if (!newName || newName === entry.name) {
      setContextMenu(null);
      return;
    }

    try {
      await commanderApi.renameFile(disk, entry.path, newName);
      refresh();
    } catch (err: any) {
      setError(err.message);
    }
    setContextMenu(null);
  };

  const handleNewFolder = async () => {
    if (!disk) return;
    const name = prompt('Enter folder name:');
    if (!name) return;

    try {
      const path = currentPath === '/' ? `/${name}` : `${currentPath}/${name}`;
      await commanderApi.createDirectory(disk, path);
      refresh();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // No disk mounted state
  if (!disk) {
    return (
      <div className="flex-1 flex flex-col bg-gray-900">
        <div className="flex items-center justify-between p-2 border-b border-gray-700">
          <span className="text-gray-400 text-sm">File Manager</span>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-1"
            title="Close file panel"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center text-gray-500">
          <div className="text-center">
            <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <p>No disk mounted</p>
            <p className="text-sm mt-1">Say "insert disk [name]" to mount one</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex-1 flex flex-col bg-gray-900 relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Toolbar */}
      <div className="flex-shrink-0 flex items-center gap-2 p-2 border-b border-gray-700">
        {/* Navigate up */}
        <button
          onClick={navigateUp}
          disabled={currentPath === '/'}
          className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          title="Go up"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>

        {/* Breadcrumb */}
        <div className="flex-1 flex items-center gap-1 text-sm overflow-x-auto">
          <button
            onClick={() => navigateTo('/')}
            className="text-gray-400 hover:text-white px-1"
          >
            {disk}
          </button>
          {pathParts.map((part, i) => (
            <span key={i} className="flex items-center gap-1">
              <span className="text-gray-600">/</span>
              <button
                onClick={() => navigateTo('/' + pathParts.slice(0, i + 1).join('/'))}
                className="text-gray-400 hover:text-white px-1"
              >
                {part}
              </button>
            </span>
          ))}
        </div>

        {/* Refresh */}
        <button
          onClick={refresh}
          className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
          title="Refresh"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>

        {/* View mode toggle */}
        <div className="flex items-center border border-gray-600 rounded">
          <button
            onClick={() => setViewMode('list')}
            className={`p-1.5 ${viewMode === 'list' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}
            title="List view"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`p-1.5 ${viewMode === 'grid' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}
            title="Grid view"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </button>
        </div>

        {/* New folder */}
        <button
          onClick={handleNewFolder}
          className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
          title="New folder"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          </svg>
        </button>

        {/* Upload button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
          title="Upload files"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />

        {/* Close button */}
        <button
          onClick={onClose}
          className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
          title="Close file panel"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex-shrink-0 bg-red-900/50 border-b border-red-500 text-red-200 px-3 py-2 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-200">
            Dismiss
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex-shrink-0 flex items-center justify-center p-2 text-gray-400">
          <svg className="w-5 h-5 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading...
        </div>
      )}

      {/* File List */}
      {!loading && (
        <FileList
          entries={entries}
          viewMode={viewMode}
          selectedPaths={selectedPaths}
          onSelect={handleSelect}
          onOpen={handleOpen}
          onContextMenu={handleContextMenu}
        />
      )}

      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 bg-blue-500/20 border-2 border-dashed border-blue-500 flex items-center justify-center z-10">
          <div className="text-center text-blue-200">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-lg font-medium">Drop files here to upload</p>
          </div>
        </div>
      )}

      {/* Upload progress */}
      {uploadProgress.length > 0 && (
        <div className="absolute bottom-4 right-4 bg-gray-800 rounded-lg shadow-lg border border-gray-600 p-4 w-80 z-20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-white">Uploading files...</span>
            <span className="text-sm text-gray-400">
              {uploadProgress.filter(p => p.status === 'completed').length}/{uploadProgress.length}
            </span>
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {uploadProgress.map((p, i) => (
              <div key={i} className="text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-300 truncate flex-1">{p.filename}</span>
                  <span className={`text-xs ${
                    p.status === 'completed' ? 'text-green-400' :
                    p.status === 'error' ? 'text-red-400' :
                    p.status === 'uploading' ? 'text-blue-400' :
                    'text-gray-500'
                  }`}>
                    {p.status === 'completed' ? 'Done' :
                     p.status === 'error' ? 'Error' :
                     p.status === 'uploading' ? `${p.progress}%` :
                     'Pending'}
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-1">
                  <div
                    className={`h-1 rounded-full transition-all ${
                      p.status === 'completed' ? 'bg-green-500' :
                      p.status === 'error' ? 'bg-red-500' :
                      'bg-blue-500'
                    }`}
                    style={{ width: `${p.progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Context menu */}
      {contextMenu && (
        <div
          className="fixed bg-gray-800 border border-gray-600 rounded-lg shadow-lg py-1 z-50"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            onClick={() => {
              if (contextMenu.entry.is_dir) {
                navigateTo(contextMenu.entry.path);
              } else {
                setPreviewFile(contextMenu.entry);
              }
              setContextMenu(null);
            }}
            className="w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700"
          >
            {contextMenu.entry.is_dir ? 'Open' : 'Preview'}
          </button>
          {!contextMenu.entry.is_dir && (
            <button
              onClick={() => {
                if (disk) {
                  commanderApi.downloadFile(disk, contextMenu.entry.path, contextMenu.entry.name);
                }
                setContextMenu(null);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700"
            >
              Download
            </button>
          )}
          <button
            onClick={handleRename}
            className="w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700"
          >
            Rename
          </button>
          <div className="border-t border-gray-700 my-1" />
          <button
            onClick={handleDelete}
            className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-700"
          >
            Delete
          </button>
        </div>
      )}

      {/* File Preview Modal */}
      {previewFile && disk && (
        <FilePreview
          disk={disk}
          path={previewFile.path}
          fileName={previewFile.name}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </div>
  );
}
